import { PureComponent, createElement } from 'react';
import { DragSource, DropTarget } from 'react-dnd';

import classNames from 'classnames';
import { Button } from 'components/Button/Button';
import get from 'lodash/get';
import PropTypes from 'prop-types';
import handleEvent from 'utils/handleEvent';
import EditWidget from './EditWidget';
import { WIDGET } from './ItemTypes';

const columnSource = {
  beginDrag(props) {
    return {
      type: 'column',
      rowIndex: props.rowIndex,
      columnIndex: props.columnIndex,
      columnsLength: props.columnsLength,
    };
  },
  canDrag(props) {
    return props.editable;
  },
};

const columnTarget = {
  drop(props, monitor) {
    const { rowIndex, columnIndex, onSortColumns, onMoveWidget } = props;
    const item = monitor.getItem();
    if (
      item.type !== 'column' &&
      (item.columnIndex !== columnIndex || item.rowIndex !== rowIndex)
    ) {
      onMoveWidget({
        initialLocation: {
          rowIndex: item.rowIndex,
          columnIndex: item.columnIndex,
          widgetIndex: item.widgetIndex,
        },
        destination: {
          rowIndex,
          columnIndex,
        },
        widgetName: item.widgetName,
        widgetProps: item.widgetProps,
      });
    }
    if (item.type === 'column') {
      const dragIndex = item.columnIndex;
      const hoverIndex = props.columnIndex;

      if (dragIndex === hoverIndex) {
        return;
      }
      if (item.rowIndex === rowIndex) {
        onSortColumns({
          initialLocation: {
            rowIndex,
            columnIndex: dragIndex,
          },
          destination: {
            rowIndex,
            columnIndex: hoverIndex,
          },
        });
        item.columnIndex = hoverIndex;
      }
    }
  },
  canDrop(props, monitor) {
    const item = monitor.getItem();
    if (item.type === 'column') {
      const { rowIndex } = props;
      if (item.rowIndex !== rowIndex) {
        return props.columnsLength < 3;
      }
    }
    return true;
  },
};

/**
 * Colum of the dashboard grid. A column holds multiple widgets.
 */
@DropTarget(WIDGET, columnTarget, (connect, monitor) => ({
  connectDropTarget: connect.dropTarget(),
  isOver: monitor.isOver(),
  canDrop: monitor.canDrop(),
}))
@DragSource(WIDGET, columnSource, (connect, monitor) => ({
  connectDragSource: connect.dragSource(),
  connectDragPreview: connect.dragPreview(),
  isDragging: monitor.isDragging(),
}))
class Column extends PureComponent {
  static propTypes = {
    addWidgetComponent: PropTypes.func,
    canDrop: PropTypes.bool,
    children: PropTypes.node,
    className: PropTypes.string,
    columnIndex: PropTypes.number,
    columnOrientation: PropTypes.string,
    connectDragPreview: PropTypes.func,
    connectDragSource: PropTypes.func,
    connectDropTarget: PropTypes.func,
    droppableColumnClass: PropTypes.string,
    editable: PropTypes.bool,
    editableColumnClass: PropTypes.string,
    isOver: PropTypes.bool,
    msg: PropTypes.object.isRequired,
    onAdd: PropTypes.func,
    onUpdateColumnProperty: PropTypes.func.isRequired,
    rowIndex: PropTypes.number,
    title: PropTypes.string,
    titlePlaceholder: PropTypes.string.isRequired,
  };

  static defaultProps = {
    columnOrientation: 'vertical',
    editableColumnClass: 'editable-column',
    droppableColumnClass: 'droppable-column',
    title: '',
  };

  state = {
    editTitle: false,
  };

  handleAdd = handleEvent(() => {
    const { rowIndex, columnIndex, onAdd } = this.props;
    onAdd(rowIndex, columnIndex);
  });

  handleInputChange = handleEvent(({ target: { value } }) => {
    const { onUpdateColumnProperty, rowIndex, columnIndex } = this.props;
    onUpdateColumnProperty(rowIndex, columnIndex, 'title', value);
  });

  handleOrientationChange = handleEvent((orientation) => {
    const { onUpdateColumnProperty, rowIndex, columnIndex } = this.props;
    onUpdateColumnProperty(
      rowIndex,
      columnIndex,
      'columnOrientation',
      orientation,
    );
  });

  handleEditClick = handleEvent(() => {
    this.setState({ editTitle: true });
  });

  handleSubmit = handleEvent((e) => {
    e.preventDefault();
    this.handleSaveClick();
  });

  handleSaveClick = handleEvent(() => {
    this.setState({ editTitle: false });
  });

  // Moves cursor of the input at the end of the input
  handleInputFocus = handleEvent((e) => {
    const val = e.target.value;
    e.target.value = '';
    e.target.value = val;
  });

  // biome-ignore lint/complexity/noExcessiveCognitiveComplexity: hic sunt dracones
  render() {
    const {
      addWidgetComponent,
      canDrop,
      children,
      className,
      columnOrientation,
      connectDragPreview,
      connectDragSource,
      connectDropTarget,
      droppableColumnClass,
      editable,
      editableColumnClass,
      isOver,
      msg,
      title,
      titlePlaceholder,
    } = this.props;
    const { editTitle } = this.state;

    let classes = `dazzle-col ${className}`;
    classes = editable
      ? `dazzle-col ${className} ${editableColumnClass}`
      : classes;
    const isActive = isOver && canDrop;
    classes = isActive
      ? `dazzle-col ${classes} ${droppableColumnClass}`
      : classes;

    let editWidgetComponentToUse = null;
    if (addWidgetComponent) {
      editWidgetComponentToUse = createElement(addWidgetComponent, {
        text: get(msg, ['dazzle', 'addNewWidget']),
        onClick: this.handleAdd,
      });
    } else {
      editWidgetComponentToUse = (
        <EditWidget
          columnOrientation={columnOrientation}
          onAdd={this.handleAdd}
          onOrientationChange={this.handleOrientationChange}
          msg={msg}
        />
      );
    }

    const renderTitle = title || get(msg, ['dazzle', 'untitled']);

    const contentElement = (
      <div
        className={`dazzle-column-header${editable ? ' grab' : ''}`}
        data-testid='dazzle-column-header'
      >
        {(editable && !editTitle) || !editable ? (
          <h4
            className={classNames(
              title ? '' : 'is-untitled',
              'dazzle__column-title',
              editable ? 'cursor-pointer' : 'cursor-auto',
            )}
            onClick={editable ? this.handleEditClick : undefined}
            data-testid='dazzle-column-title'
          >
            {renderTitle}
          </h4>
        ) : (
          <form onSubmit={this.handleSubmit}>
            <input
              type='text'
              autoFocus
              className='form-control input-sm dazzle__title-input'
              onBlur={this.handleSaveClick}
              onChange={this.handleInputChange}
              onFocus={this.handleInputFocus}
              placeholder={titlePlaceholder}
              value={title}
            />
          </form>
        )}
        {editable && !editTitle ? (
          <Button
            className='ml-2'
            icon='fa-pen'
            onClick={this.handleEditClick}
            size='xxs'
            variant='tertiary'
          />
        ) : null}
        {editable && editTitle ? (
          <Button
            className='ml-2'
            icon='fa-check'
            onClick={this.handleSaveClick}
            size='xxs'
            variant='tertiary'
          />
        ) : null}
        {editable && editWidgetComponentToUse}
      </div>
    );

    const content = editTitle
      ? contentElement
      : connectDragSource(contentElement);

    return connectDropTarget(
      connectDragPreview(
        <div className={classes}>
          <div className='dazzle__column-wrapper'>
            {title === '' && !editable ? null : content}
            {children}
          </div>
        </div>,
      ),
    );
  }
}

export default Column;
